import React, { useState, useEffect } from 'react';
import { DataTable } from '@/components/DataTable';
import { medicinesService } from '@/services/medicines';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function PharmacyPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [newMed, setNewMed] = useState({
    name: '',
    category: '',
    stock: '',
    minStock: '',
    price: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await medicinesService.getAll();
        setMedicines(r.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    })();
  }, []);

  const columns = [
    { key: 'name', header: 'Medicine', sortable: true },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (value) => <Badge variant="outline">{value}</Badge>,
    },
    {
      key: 'stock',
      header: 'Stock',
      sortable: true,
      render: (value, row) => (
        <span className={value < row.minStock ? 'text-destructive font-medium' : ''}>
          {value}
          {value < row.minStock && ' ⚠️'}
        </span>
      ),
    },
    { key: 'minStock', header: 'Min Stock', sortable: true },
    {
      key: 'price',
      header: 'Price',
      sortable: true,
      render: (value) => `$${value}`,
    },
  ];

  return (
    <div className="space-y-6" data-testid="pharmacy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Pharmacy</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage medicine inventory and stock
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-medicine-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Add Medicine
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Medicine</DialogTitle>
              <DialogDescription>
                Add a new medicine to inventory
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="med-name">Medicine Name</Label>
                <Input
                  id="med-name"
                  placeholder="Paracetamol 500mg"
                  value={newMed.name}
                  onChange={(e) => setNewMed((m) => ({ ...m, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="med-category">Category</Label>
                <Input
                  id="med-category"
                  placeholder="Pain Relief"
                  value={newMed.category}
                  onChange={(e) => setNewMed((m) => ({ ...m, category: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="med-stock">Stock Quantity</Label>
                  <Input
                    id="med-stock"
                    type="number"
                    placeholder="100"
                    value={newMed.stock}
                    onChange={(e) => setNewMed((m) => ({ ...m, stock: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="med-min">Min stock</Label>
                  <Input
                    id="med-min"
                    type="number"
                    placeholder="50"
                    value={newMed.minStock}
                    onChange={(e) => setNewMed((m) => ({ ...m, minStock: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="med-price">Price ($)</Label>
                  <Input
                    id="med-price"
                    type="number"
                    step="0.01"
                    placeholder="5.99"
                    value={newMed.price}
                    onChange={(e) => setNewMed((m) => ({ ...m, price: e.target.value }))}
                  />
                </div>
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    await medicinesService.create({
                      name: newMed.name,
                      category: newMed.category,
                      stock: Number(newMed.stock) || 0,
                      minStock: Number(newMed.minStock) || 0,
                      price: Number(newMed.price) || 0,
                    });
                    toast.success('Medicine added successfully');
                    setIsDialogOpen(false);
                    setNewMed({ name: '', category: '', stock: '', minStock: '', price: '' });
                    const r = await medicinesService.getAll();
                    setMedicines(r.data);
                  } catch (error) {
                    toast.error(getApiErrorMessage(error));
                  }
                }}
              >
                Add Medicine
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable data={medicines} columns={columns} searchPlaceholder="Search medicines..." />
    </div>
  );
}